// Holidays — WP plugin parity (Screen E5).
// Employees see a clean, read-only list of UPCOMING holidays.
// Admins (SUPER_ADMIN) see ALL holidays (past + future) with a per-row Delete,
// so a wrongly-dated entry is never hidden and can be removed. Adding holidays
// still happens in Settings ("Holiday Name|YYYY-MM-DD" bulk sync).

import React, { useEffect, useState } from "react";
import { useToast } from "@chakra-ui/react";
import { api } from "../../lib/api";
import { useShellHeader } from "../../Components/AppShell";
import { Card } from "../../Components/ui";

export default function HolidaysPage() {
  const toast = useToast();
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const isAdmin = (localStorage.getItem("role") || "") === "SUPER_ADMIN";

  useShellHeader({ title: isAdmin ? "Holidays" : "Upcoming Holidays" }, [isAdmin]);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await api("/hr/holidays");
        if (!cancel) {
          const all = (r.holidays || []).slice().sort((a, b) => new Date(a.holidayDate) - new Date(b.holidayDate));
          if (isAdmin) {
            // Show every holiday so a wrongly-dated one can always be found/removed.
            setHolidays(all);
          } else {
            const today = new Date(); today.setHours(0, 0, 0, 0);
            setHolidays(all.filter((h) => new Date(h.holidayDate) >= today));
          }
        }
      } catch (e) { if (!cancel) toast({ title: e.message, status: "error" }); }
      finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleDelete(h) {
    const dateStr = new Date(h.holidayDate).toISOString().slice(0, 10);
    if (!window.confirm(`Delete holiday "${h.title}" (${dateStr})?`)) return;
    setDeletingId(h._id);
    try {
      await api(`/hr/holidays/${h._id}`, { method: "DELETE" });
      setHolidays((prev) => prev.filter((x) => x._id !== h._id));
      toast({ title: "Holiday deleted", status: "success" });
    } catch (e) {
      toast({ title: e.message || "Delete failed", status: "error" });
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <h2 style={{ fontSize: 22, fontWeight: 700, color: "var(--text-primary)", margin: 0 }}>
        {isAdmin ? "Holidays" : "Upcoming Holidays"}
      </h2>
      <Card padding="none">
        {loading ? (
          <div style={{ padding: 24, color: "var(--text-tertiary)", textAlign: "center" }}>Loading…</div>
        ) : holidays.length === 0 ? (
          <div style={{ padding: 24, color: "var(--text-tertiary)", textAlign: "center" }}>
            {isAdmin ? "No holidays." : "No upcoming holidays."}
          </div>
        ) : (
          <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
            {holidays.map((h, i) => (
              <li
                key={h._id || i}
                style={{
                  padding: "14px 18px",
                  borderTop: i === 0 ? "none" : "1px solid var(--border-subtle)",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 12,
                }}
              >
                <span style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>{h.title}</span>
                <span style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <span className="tabular" style={{ fontSize: 13, color: "var(--text-tertiary)" }}>
                    {new Date(h.holidayDate).toISOString().slice(0, 10)}
                  </span>
                  {isAdmin && (
                    <button
                      onClick={() => handleDelete(h)}
                      disabled={deletingId === h._id}
                      title="Delete holiday"
                      style={{
                        border: "1px solid var(--border-subtle)",
                        background: "transparent",
                        color: "var(--tone-danger-fg)",
                        fontSize: 12,
                        fontWeight: 600,
                        padding: "4px 10px",
                        borderRadius: 6,
                        cursor: deletingId === h._id ? "default" : "pointer",
                        opacity: deletingId === h._id ? 0.5 : 1,
                      }}
                    >
                      {deletingId === h._id ? "Deleting…" : "Delete"}
                    </button>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
